// MobileStudentApp.jsx
// Bottom-tab native-feel shell for student mobile experience
// Wraps existing components in a mobile-optimized nav structure
// Detects Capacitor native context and adjusts accordingly

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  green:"#0a6e4e", greenD:"#084f38", greenL:"#e8f5f0",
  gold:"#f5a623", navy:"#0f1923", inkM:"#2d3d4f", inkL:"#6b7a8d",
  border:"#e2e8e5", surface:"#f7f9f8", white:"#ffffff",
  red:"#dc2626",
};

// ── Capacitor plugin helpers ───────────────────────────────────────────────
async function hideSplash() {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 400 });
  } catch { /* not native */ }
}

async function setBadge(count) {
  try {
    const { Badge } = await import("@capawesome/capacitor-badge");
    await Badge.set({ count });
  } catch { /* not available */ }
}

async function clearBadge() {
  try {
    const { Badge } = await import("@capawesome/capacitor-badge");
    await Badge.clear();
  } catch { /* not available */ }
}

async function setupBackButton(handler) {
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", handler);
  } catch { /* not native */ }
}

// Detect if running inside Capacitor native app
const isNative = () => {
  try { return !!(window.Capacitor?.isNativePlatform?.()); }
  catch { return false; }
};

// ── Safe area heights (notch + home indicator) ─────────────────────────────
const SAFE_TOP    = isNative() ? 44 : 0;
const SAFE_BOTTOM = isNative() ? 34 : 0;
const TAB_H       = 64 + SAFE_BOTTOM;

// ── Register push token ────────────────────────────────────────────────────
async function registerPushToken(userId) {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.register();
    PushNotifications.addListener("registration", async ({ value: token }) => {
      await supabase.from("push_tokens").upsert(
        { user_id: userId, token, platform: "android", updated_at: new Date().toISOString() },
        { onConflict: "user_id,token" }
      );
    });
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("Push received:", notification);
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification.data;
      if (data?.page) window.dispatchEvent(new CustomEvent("ojthub:setPage", { detail: data.page }));
    });
  } catch (e) {
    // Not in native context — skip silently
  }
}

// ── Offline logbook draft manager ─────────────────────────────────────────
export const offlineDrafts = {
  key: "ojthub_logbook_drafts",
  getAll() {
    try { return JSON.parse(localStorage.getItem(this.key) || "{}"); }
    catch { return {}; }
  },
  save(date, data) {
    const all = this.getAll();
    all[date] = { ...data, savedAt: new Date().toISOString(), synced: false };
    localStorage.setItem(this.key, JSON.stringify(all));
  },
  markSynced(date) {
    const all = this.getAll();
    if (all[date]) { all[date].synced = true; localStorage.setItem(this.key, JSON.stringify(all)); }
  },
  getUnsynced() {
    return Object.entries(this.getAll()).filter(([, v]) => !v.synced).map(([date, data]) => ({ date, ...data }));
  },
  clear(date) {
    const all = this.getAll();
    delete all[date];
    localStorage.setItem(this.key, JSON.stringify(all));
  },
};

// ── Sync offline drafts to Supabase ───────────────────────────────────────
export async function syncOfflineDrafts(userId, notify) {
  const unsynced = offlineDrafts.getUnsynced();
  if (!unsynced.length) return;
  let synced = 0;
  let failed = 0;
  for (const draft of unsynced) {
    try {
      const { error } = await supabase.from("logbook_entries").upsert({
        student_id:           userId,
        entry_date:           draft.date,
        tasks:                draft.tasks || "",
        mood:                 typeof draft.mood === "number" ? draft.mood : 3,
        hours:                parseFloat(draft.hours) || 8,
        learnings:            draft.learnings || "",
        challenges:           draft.challenges || "",
        supervisor_verified:  false,
        coordinator_approved: false,
        created_at:           draft.savedAt || new Date().toISOString(),
      }, { onConflict: "student_id,entry_date" });
      if (!error) {
        offlineDrafts.clear(draft.date); // fully remove instead of just marking synced
        synced++;
      } else {
        console.error("Sync failed for", draft.date, error.message);
        failed++;
      }
    } catch(e) {
      console.error("Sync error for", draft.date, e.message);
      failed++;
    }
  }
  if (synced > 0 && notify) notify(`${synced} offline entr${synced > 1 ? "ies" : "y"} synced ✓`);
  if (failed > 0 && notify) notify(`${failed} entr${failed > 1 ? "ies" : "y"} failed to sync — will retry`);
}

// ── Bottom Tab Bar ─────────────────────────────────────────────────────────
function BottomTabBar({ page, setPage, unreadCount, draftCount }) {
  const TABS = [
    { id:"home",     icon:"🏠", label:"Home"      },
    { id:"jobs",     icon:"💼", label:"Jobs"      },
    { id:"logbook",  icon:"📓", label:"Logbook",  badge: draftCount  },
    { id:"progress", icon:"📊", label:"Progress"  },
    { id:"profile",  icon:"👤", label:"Profile"   },
  ];

  return (
    <div style={{
      position:"fixed", bottom:0, left:0, right:0, zIndex:200,
      background:"#fff",
      borderTop:"1px solid #e8ede9",
      display:"flex",
      height: TAB_H,
      paddingBottom: SAFE_BOTTOM,
      boxShadow:"0 -4px 24px rgba(10,110,78,.07)",
    }}>
      {TABS.map(tab => {
        const active = page === tab.id;
        return (
          <button key={tab.id} onClick={() => setPage(tab.id)}
            style={{
              flex:1, display:"flex", flexDirection:"column",
              alignItems:"center", justifyContent:"center",
              background:"none", border:"none", cursor:"pointer",
              padding:"6px 0 4px", gap:2, position:"relative",
              fontFamily:"'DM Sans',sans-serif",
            }}>
            {/* Active indicator pill */}
            {active && (
              <div style={{
                position:"absolute", top:0, left:"50%", transform:"translateX(-50%)",
                width:32, height:3, borderRadius:"0 0 4px 4px",
                background:`linear-gradient(90deg,${C.green},#16a34a)`,
              }} />
            )}
            {/* Badge */}
            {tab.badge > 0 && (
              <div style={{
                position:"absolute", top:6, right:"calc(50% - 16px)",
                background:C.gold, color:C.navy,
                width:16, height:16, borderRadius:"50%",
                fontSize:9, fontWeight:900,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>{tab.badge > 9 ? "9+" : tab.badge}</div>
            )}
            <span style={{ fontSize:active ? 22 : 20, lineHeight:1,
              transition:"transform .15s",
              transform: active ? "scale(1.1)" : "scale(1)" }}>
              {tab.icon}
            </span>
            <span style={{
              fontSize:10, fontWeight: active ? 800 : 500,
              color: active ? C.green : C.inkL,
              transition:"color .15s",
            }}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}


// ── Network Status Bar ────────────────────────────────────────────────────
function NetworkBar() {
  const [online, setOnline] = useState(navigator.onLine);
  const [justBack, setJustBack] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setJustBack(true);
      setTimeout(() => setJustBack(false), 3000);
    };
    const handleOffline = () => { setOnline(false); setJustBack(false); };
    window.addEventListener("online",  handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online",  handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (online && !justBack) return null;

  return (
    <div style={{
      position:"fixed", top: SAFE_TOP, left:0, right:0, zIndex:300,
      background: online ? "#16a34a" : "#1a1a2e",
      color:"#fff", padding:"8px 16px",
      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
      fontSize:12, fontWeight:700,
      animation:"slideDown .3s ease",
    }}>
      <style>{`@keyframes slideDown{from{opacity:0;transform:translateY(-100%)}to{opacity:1;transform:none}}`}</style>
      <span style={{ width:7, height:7, borderRadius:"50%",
        background: online ? "#4ade80" : "#ef4444",
        display:"inline-block", flexShrink:0 }} />
      {online ? "Back online — syncing your drafts…" : "You're offline — entries will save locally"}
    </div>
  );
}

// ── Offline Draft Banner ───────────────────────────────────────────────────
function OfflineDraftBanner({ userId, notify }) {
  const [drafts, setDrafts] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setDrafts(offlineDrafts.getUnsynced());
    const interval = setInterval(() => setDrafts(offlineDrafts.getUnsynced()), 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-sync when online
  useEffect(() => {
    const handleOnline = async () => {
      if (userId && offlineDrafts.getUnsynced().length > 0) {
        setSyncing(true);
        await syncOfflineDrafts(userId, notify);
        setDrafts(offlineDrafts.getUnsynced());
        setSyncing(false);
      }
    };
    window.addEventListener("online", handleOnline);
    // Also try sync every 30 seconds in case 'online' event was missed
    const interval = setInterval(() => {
      if (navigator.onLine && userId && offlineDrafts.getUnsynced().length > 0) {
        handleOnline();
      }
    }, 30000);
    return () => {
      window.removeEventListener("online", handleOnline);
      clearInterval(interval);
    };
  }, [userId]); // eslint-disable-line

  if (drafts.length === 0) return null;

  return (
    <div style={{
      position:"fixed", top: SAFE_TOP + 52, left:12, right:12, zIndex:150,
      background: syncing ? C.green : C.navy,
      borderRadius:12, padding:"10px 14px",
      display:"flex", alignItems:"center", gap:10,
      boxShadow:"0 4px 20px rgba(0,0,0,.2)",
      animation:"slideDown .3s ease",
    }}>
      <style>{`@keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:none}}`}</style>
      <span style={{ fontSize:18 }}>{syncing ? "🔄" : "📴"}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:12, fontWeight:800, color:"#fff" }}>
          {syncing ? "Syncing drafts…" : `${drafts.length} offline draft${drafts.length > 1 ? "s" : ""}`}
        </div>
        <div style={{ fontSize:11, color:"rgba(255,255,255,.6)" }}>
          {syncing ? "Uploading to server" : "Will sync when online"}
        </div>
      </div>
      {!syncing && navigator.onLine && (
        <button onClick={async () => {
          setSyncing(true);
          await syncOfflineDrafts(userId, notify);
          setDrafts(offlineDrafts.getUnsynced());
          setSyncing(false);
        }} style={{
          background:"rgba(255,255,255,.15)", border:"none", borderRadius:8,
          padding:"6px 12px", color:"#fff", fontSize:11, fontWeight:700,
          cursor:"pointer", fontFamily:"inherit",
        }}>
          Sync now
        </button>
      )}
    </div>
  );
}

// ── Haptic feedback helper ─────────────────────────────────────────────────
export async function haptic(style = "light") {
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[style] || ImpactStyle.Light });
  } catch { /* not native */ }
}

// ── Main Mobile Shell ──────────────────────────────────────────────────────
export default function MobileStudentApp({ user, children, page, setPage, notify }) {
  const draftCount = offlineDrafts.getUnsynced().length;

  // Register push, hide splash, setup back button
  useEffect(() => {
    // Hide splash screen
    hideSplash();

    if (user?.id && isNative()) registerPushToken(user.id);

    // Android hardware back button
    setupBackButton(() => {
      if (page === "home" || page === "jobs") {
        // On home tab — confirm exit
        if (window.confirm("Exit OJT Hub?")) {
          try { window.Capacitor?.Plugins?.App?.exitApp(); } catch { window.close(); }
        }
      } else {
        // Go back to home
        setPage("home");
      }
    });

    // Clear badge when app opens
    clearBadge();
  }, []); // eslint-disable-line

  // Sync offline drafts on mount if online
  useEffect(() => {
    if (user?.id && navigator.onLine) syncOfflineDrafts(user.id, notify);
  }, [user?.id]); // eslint-disable-line

  // Update badge when draft count changes
  useEffect(() => {
    if (draftCount > 0) setBadge(draftCount);
    else clearBadge();
  }, [draftCount]);

  return (
    <div style={{
      minHeight:"100vh",
      paddingTop: SAFE_TOP,
      paddingBottom: TAB_H,
      paddingTop: SAFE_TOP,
      background: C.surface,
      fontFamily:"'DM Sans',sans-serif",
      maxWidth: 480,
      margin:"0 auto",
      position:"relative",
    }}>
      <NetworkBar />
      <OfflineDraftBanner userId={user?.id} notify={notify} />
      {children}
      <BottomTabBar page={page} setPage={setPage} draftCount={draftCount} />
    </div>
  );
}
